import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { TypePill } from '@/components/synapse/TypePill';
import { ScanDialog } from '@/components/synapse/ScanDialog';
import { ItemSearchField } from '@/components/synapse/ItemSearchField';
import { useItemByBarcode } from '@/hooks/useInventory';
import type { BatchWithBalance, ItemDetail } from '@/types/api';
import { ApiError } from '@/lib/api';
import type { DraftSelection, IssueLines } from './useIssueLines';

interface IssueLineEntryProps {
  lines: IssueLines;
  /**
   * i18n key for the button that adds the picked medicine to the caller's list.
   * The list is a "release" on the Issue screen and a visit on the visit form,
   * and the button should say which.
   */
  addLabelKey?: string;
}

/**
 * The entry half of the picker: scan a barcode or search by name, then choose
 * the batch and quantity and add the line. Stateless beyond the scan dialog's
 * text — everything it shows and changes lives in `useIssueLines`.
 */
export function IssueLineEntry({ lines, addLabelKey = 'issue.addToRelease' }: IssueLineEntryProps) {
  const { t } = useTranslation();
  const { draft } = lines;
  return (
    <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
        <button
          type="button"
          onClick={lines.openScan}
          className="flex h-[88px] flex-1 items-center gap-4 rounded-lg bg-accent px-5 text-left text-accent-foreground transition-opacity hover:opacity-90"
        >
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-white/10">
            <Icon name="scan" size={24} />
          </span>
          <span className="flex flex-col">
            <span className="text-[16px] font-semibold tracking-[-0.02em]">
              {t('issue.scanBarcode')}
            </span>
            <span className="mt-0.5 text-xs opacity-75">{t('issue.scanHint')}</span>
          </span>
        </button>
        <div className="hidden w-[200px] flex-col justify-center gap-1 rounded-lg border border-dashed border-line-2 px-4 py-3.5 sm:flex">
          <div className="text-[13px] font-semibold">{t('issue.orSearch')}</div>
          <div className="text-[11.5px] text-muted">{t('issue.orSearchHint')}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3.5">
        <ItemSearchField onPick={lines.pickItem} disabledItemId={draft?.item.id ?? null} />

        {draft && (
          <DraftEditor
            draft={draft}
            detail={lines.detail}
            detailLoading={lines.detailLoading}
            quantityValid={lines.quantityValid}
            onPickBatch={lines.pickBatch}
            onChangeQuantity={lines.setDraftQuantity}
            onCancel={lines.cancelDraft}
            onAdd={lines.addRow}
            addLabelKey={addLabelKey}
          />
        )}
      </div>

      {lines.scanOpen && (
        <IssueScanDialog onClose={lines.closeScan} onResolved={lines.resolveScan} />
      )}
    </>
  );
}

interface DraftEditorProps {
  draft: DraftSelection;
  detail: ItemDetail | undefined;
  detailLoading: boolean;
  quantityValid: boolean;
  onPickBatch: (batch: BatchWithBalance) => void;
  onChangeQuantity: (q: number) => void;
  onCancel: () => void;
  onAdd: () => void;
  addLabelKey: string;
}

function DraftEditor({
  draft,
  detail,
  detailLoading,
  quantityValid,
  onPickBatch,
  onChangeQuantity,
  onCancel,
  onAdd,
  addLabelKey,
}: DraftEditorProps) {
  const { t } = useTranslation();
  const stockedBatches = useMemo(
    () => (detail?.batches ?? []).filter((b) => b.balance > 0),
    [detail],
  );
  const multiBatch = stockedBatches.length > 1;

  return (
    <div className="rounded-md border border-line bg-paper-2 p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[14px] font-semibold tracking-[-0.01em]">
              {draft.item.brandName}
            </span>
            <span className="text-[13px] text-muted">{draft.item.genericName}</span>
            {draft.item.strength && (
              <span className="font-mono text-[13px] text-ink-2 tabular-nums">
                · {draft.item.strength}
              </span>
            )}
          </div>
          <div className="mt-1.5">
            <TypePill type={draft.item.type} size="sm" />
          </div>
        </div>
        <button
          type="button"
          aria-label={t('issue.cancelSelection')}
          onClick={onCancel}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
        <Field
          label={t('common.batch')}
          hint={
            detailLoading
              ? t('issue.batchLoadingHint')
              : multiBatch
              ? t('issue.batchDefaultHint')
              : undefined
          }
        >
          {detailLoading ? (
            <div className="h-10 animate-pulse rounded-md bg-paper" />
          ) : stockedBatches.length === 0 ? (
            <div className="rounded-md border border-line bg-paper px-3 py-2 text-[12.5px] text-destructive">
              {t('issue.noStockedBatches')}
            </div>
          ) : !multiBatch ? (
            <div className="rounded-md border border-line bg-paper px-3 py-2">
              <BatchSummary batch={stockedBatches[0]!} />
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {stockedBatches.map((b) => {
                const active = draft.batch?.id === b.id;
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => onPickBatch(b)}
                      className={
                        'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors ' +
                        (active
                          ? 'border-accent bg-paper'
                          : 'border-line bg-paper hover:bg-paper-2')
                      }
                    >
                      <BatchSummary batch={b} />
                      {active && (
                        <Icon name="check" size={14} className="ml-auto text-success" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Field>

        <Field
          label={t('common.quantity')}
          hint={
            draft.batch
              ? t('issue.upToBalance', { balance: draft.batch.balance })
              : undefined
          }
        >
          <div className="flex gap-1.5">
            <button
              type="button"
              aria-label={t('common.decreaseQuantity')}
              onClick={() => onChangeQuantity(Math.max(1, draft.quantity - 1))}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
            >
              <Icon name="minus" size={16} />
            </button>
            <Input
              mono
              className="flex-1 text-center"
              type="number"
              aria-label={t('common.quantity')}
              min={1}
              max={draft.batch?.balance}
              value={draft.quantity}
              onChange={(e) =>
                onChangeQuantity(Math.max(1, Number(e.target.value) || 0))
              }
            />
            <button
              type="button"
              aria-label={t('common.increaseQuantity')}
              onClick={() =>
                onChangeQuantity(
                  draft.batch
                    ? Math.min(draft.batch.balance, draft.quantity + 1)
                    : draft.quantity + 1,
                )
              }
              className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-paper text-ink hover:bg-paper-2"
            >
              <Icon name="plus" size={16} />
            </button>
          </div>
        </Field>
      </div>

      {draft.batch && draft.quantity > draft.batch.balance && (
        <div className="mt-2 text-[12px] text-destructive">
          {t('issue.onlyAvailable', { balance: draft.batch.balance })}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={onAdd} disabled={!quantityValid}>
          <Icon name="plus" size={16} /> {t(addLabelKey)}
        </Button>
      </div>
    </div>
  );
}

function BatchSummary({ batch }: { batch: BatchWithBalance }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <span className="font-mono text-[12.5px] text-ink tabular-nums">
        {batch.batchNumber ?? '—'}
      </span>
      <span className="font-mono text-[11.5px] text-muted tabular-nums">
        {batch.expiryDate ? t('adjust.expDate', { date: batch.expiryDate }) : t('issue.noExpiry')}
      </span>
      <span className="ml-auto font-mono text-[12px] tabular-nums text-muted">
        {t('issue.nOnHand', { count: batch.balance })}
      </span>
    </div>
  );
}

interface IssueScanDialogProps {
  onClose: () => void;
  onResolved: (item: ItemDetail) => void;
}

/**
 * Issue's scanner: looks up the barcode via the dedicated by-barcode endpoint
 * (returns ItemDetail with FEFO-ordered batches) so the caller can apply batch
 * logic immediately. On miss the dialog says so — Issue does not own a
 * create-new-item path; that belongs to Add Stock.
 */
function IssueScanDialog({ onClose, onResolved }: IssueScanDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { data: match, isFetching, error } = useItemByBarcode(code, {
    enabled: code.trim().length >= 3,
  });

  const oos = match ? match.totalStock <= 0 : false;
  const canUse = match !== undefined && match !== null && !oos;

  return (
    <ScanDialog
      onClose={onClose}
      onCodeChange={setCode}
      canUse={() => canUse}
      onUse={() => {
        if (match && !oos) onResolved(match);
      }}
      renderPreview={() => {
        if (isFetching) return <span className="text-muted">{t('common.lookingUp')}</span>;
        if (error instanceof ApiError && error.code === 'NOT_FOUND') {
          return <span className="text-muted">{t('issue.unknownBarcode')}</span>;
        }
        if (match && oos) {
          return (
            <span className="text-destructive">
              {t('issue.noAvailableStockOf', { item: match.brandName })}
            </span>
          );
        }
        if (match) {
          return (
            <span className="text-success">
              <strong className="font-semibold">{match.brandName}</strong>
              {match.strength ? ` · ${match.strength}` : ''} —{' '}
              <span className="font-mono tabular-nums">{t('issue.nOnHand', { count: match.totalStock })}</span>
            </span>
          );
        }
        return null;
      }}
    />
  );
}
